import { useCallback, useEffect } from "react";
import { PageMode } from "../../../common/types";
import {
  ElasticTabs,
  IElasticTab,
} from "../../../common/components/elasticTabs/ElasticTabs";
import { ClassWizard } from "./classWizard/ClassWizard";
import { useAppContext } from "../../../AppContext";
import { endpointsMetadata } from "../../../common/infrastructure/routes/endpointsMetadata";
import { useApiQuery } from "../../../common/infrastructure/hooks/useApiQuery";
import { IEventWizardInitialData } from "./models/eventWizardInitialDataModel";
import { useParams } from "react-router";
import { ServiceWizard } from "./serviceEventWizard/ServiceWizard";
import { BlockedTimeWizard } from "./blockedTimeWizard/BlockedTimeWizard";
import { useEmployeeCalendarData } from "../../../common/components/forms/inputs/instructorCalendar/hooks/useEmployeeCalendarData";
import { EventType, OnboardingItemKey } from "../../../common/enums";
import { useOnboarding } from "../../onboarding/OnboardingProvider";

const TAB_ONBOARDING_MAP: Record<string, OnboardingItemKey> = {
  "Услуга": OnboardingItemKey.ServiceWizardGuide,
  "Блокирано време": OnboardingItemKey.BlockedTimeGuide,
};

interface IEventWizardProps {
  pageMode: PageMode;
}

export const EventWizard = ({ pageMode }: IEventWizardProps) => {
  const { id } = useParams();
  const { selectedStudioId, showServicesTypeOptions, showGroupTypeOptions } =
    useAppContext();

  const { studioEmployees, isLoading: isEmployeesLoading } = useEmployeeCalendarData({
    selectedStudioId,
    enabled: selectedStudioId > 0,
  });

  const { data: initialData, isLoading } = useApiQuery<IEventWizardInitialData>({
    queryKey: [
      endpointsMetadata.planner.wizardGetInitialData,
      id,
      selectedStudioId,
    ],
    endpoint: `${endpointsMetadata.planner.wizardGetInitialData}?eventId=${
      id ?? 0
    }`,
    disableErrorToast: false,
    retry: false,
    globalIsLoading: !!id,
  });

  const onboarding = useOnboarding();

  // Clear help override on unmount
  useEffect(() => {
    return () => onboarding?.setHelpStepOverride(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTabChange = useCallback(
    (index: number) => {
      if (!onboarding) return;
      const availableTabs = tabs.filter((t) => !t.validator || t.validator());
      const tab = availableTabs[index];
      if (!tab) return;
      const stepKey = TAB_ONBOARDING_MAP[tab.name] ?? null;
      onboarding.setHelpStepOverride(stepKey);
      if (onboarding.isActive && stepKey && onboarding.pendingItemKeys.has(stepKey)) {
        onboarding.activateStep(stepKey);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onboarding]
  );

  const hideTabs = pageMode === "edit" || pageMode === "duplicate";

  const tabs: IElasticTab[] = [
    {
      name: "Kлас",
      children: (shouldHideNavigation) => (
        <ClassWizard
          pageMode={pageMode}
          hasTabs={!shouldHideNavigation}
          initialData={initialData}
          studioInstructors={studioEmployees}
          isLoading={isLoading}
        />
      ),
      validator: () =>
        showGroupTypeOptions &&
        (!hideTabs ||
          initialData?.currentlySelectedEvent?.type === EventType.Group ||
          initialData?.currentlySelectedEvent?.type === EventType.Individual),
    },
    {
      name: "Услуга",
      dataOnboarding: "event-wizard-service-tab",
      children: (shouldHideNavigation) => (
        <ServiceWizard
          pageMode={pageMode}
          hasTabs={!shouldHideNavigation}
          initialData={initialData}
          studioEmployees={studioEmployees}
          isLoading={isLoading || isEmployeesLoading}
        />
      ),
      validator: () =>
        showServicesTypeOptions &&
        (!hideTabs ||
          initialData?.currentlySelectedEvent?.type === EventType.Service),
    },
    {
      name: "Блокирано време",
      dataOnboarding: "event-wizard-blocked-tab",
      children: () => (
        <BlockedTimeWizard
          pageMode={pageMode}
          initialData={initialData}
          studioEmployees={studioEmployees}
          isLoading={isLoading}
        />
      ),
      validator: () =>
        showServicesTypeOptions &&
        (!hideTabs ||
          initialData?.currentlySelectedEvent?.type === EventType.BlockedTime),
    },
  ];

  return <ElasticTabs tabs={tabs} dataOnboarding="event-wizard-tabs" onTabChange={handleTabChange} />;
};
